"use client";

import { useEffect } from "react";

/**
 * Fires a GA4 `whatsapp_click` event whenever ANY WhatsApp CTA is clicked on
 * the guest site — the floating button, booking cards, contact page, blocked-
 * date CTAs, error pages, footer, etc.
 *
 * Implemented as document-level event delegation (one listener) rather than an
 * onClick on each button, because WhatsApp links (`wa.me`) live in 20+ files,
 * many server-rendered. Delegation catches every one — including links added
 * later — without touching each CTA.
 *
 * Turning this into a Google Ads conversion (one-time, in the web UIs):
 *   1. GA4 → Admin → Events → mark `whatsapp_click` as a Key event.
 *   2. GA4 → Admin → Google Ads links → link the Ads account.
 *   3. Google Ads → Goals → Conversions → Import → GA4 → import `whatsapp_click`.
 * After ~2 weeks of data, switch the campaign goal from "Website traffic" to
 * this conversion so Google optimises for inquiries, not raw clicks.
 */
export default function WhatsAppConversionTracker() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const el = e.target as Element | null;
      const link = el?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link) return;
      // `.href` is the resolved absolute URL; fall back to the raw attribute
      // for custom-scheme links like `whatsapp://`.
      const href = link.href || link.getAttribute("href") || "";
      if (!/wa\.me|api\.whatsapp\.com|whatsapp:/i.test(href)) return;

      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag !== "function") return;
      w.gtag("event", "whatsapp_click", {
        link_url: href,
        page_path: window.location.pathname,
        // `send_to` defaults to the configured GA4 property; kept implicit.
      });
    }

    // Capture phase: fire before the link opens (new tab / navigation) and
    // before any React handler can stopPropagation.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
